(function (root, factory) {
  const rules = factory();
  if (typeof module === 'object' && module.exports) module.exports = rules;
  else root.OtelloGiftRules = rules;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  const DELIVERY_PRODUCT = '15946786603392';
  const DELIVERY_VARIANT = '59882504487296';
  const GIFT_CARD_PRODUCT = '15946786636160';
  const VARIANTS = Object.freeze({
    5000: '59882504520064', 6000: '59882506977664', 7000: '59882507010432',
    8000: '59882507043200', 9000: '59882507075968', 10000: '59882507108736',
    11000: '59882507141504', 12000: '59882507174272', 13000: '59882507207040',
    14000: '59882507239808', 15000: '59882507272576', 16000: '59882507305344',
    17000: '59882507338112', 18000: '59882507370880', 19000: '59882507403648',
    20000: '59882507436416', 21000: '59882507469184', 22000: '59882507501952',
    23000: '59882507534720', 24000: '59882507567488', 25000: '59882507600256',
    26000: '59882507633024', 27000: '59882507665792', 28000: '59882507698560',
    29000: '59882507731328', 30000: '59882507764096'
  });
  // Minimum full-price unit value (pence) per vendor; vendors not listed never qualify.
  const THRESHOLDS = Object.freeze({ 'liang & eimil': 45000, 'richmond interiors': 45000, 'libra company': 60000 });

  function id(value) { return String(value || '').split('/').pop(); }
  function integer(value) { return Number.isSafeInteger(value) && value >= 0; }

  function pence(decimal) {
    if (typeof decimal !== 'string' || !/^\d+(\.\d{1,2})?$/.test(decimal)) return null;
    const [whole, fraction = ''] = decimal.split('.');
    const result = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
    return result <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(result) : null;
  }

  function promotional(line) {
    return [DELIVERY_PRODUCT, GIFT_CARD_PRODUCT].includes(id(line.productId)) ||
      id(line.variantId) === DELIVERY_VARIANT || Object.values(VARIANTS).includes(id(line.variantId));
  }

  function netLinePence(line) {
    if (!integer(line.originalLinePence) || !Array.isArray(line.discountAllocations)) return null;
    let amount = BigInt(line.originalLinePence);
    for (const allocation of line.discountAllocations) {
      if (!integer(allocation.amountPence)) return null;
      amount -= BigInt(allocation.amountPence);
    }
    return amount < 0n ? null : Number(amount);
  }

  function offer(line, currency = 'GBP') {
    const vendor = String(line.vendor || '').trim().toLowerCase();
    const threshold = Object.prototype.hasOwnProperty.call(THRESHOLDS, vendor) ? THRESHOLDS[vendor] : 0;
    if (currency !== 'GBP' || !threshold ||
        promotional(line) || line.giftCard || line.deliveryCharge ||
        (Array.isArray(line.tags) && line.tags.includes('OUTLET')) ||
        !integer(line.quantity) || line.quantity < 1) return null;
    const net = netLinePence(line);
    if (net === null || net !== line.originalLinePence) return null;
    const quantity = BigInt(line.quantity);
    if (line.compareAtPence !== null && (!integer(line.compareAtPence) ||
        BigInt(line.compareAtPence) * quantity > BigInt(line.originalLinePence))) return null;
    if (BigInt(net) < BigInt(threshold) * quantity) return null;
    const denominator = 10000n * quantity;
    const steps = (BigInt(net) + denominator - 1n) / denominator;
    const faceValuePence = Number(steps > 30n ? 30n : steps) * 1000;
    return { faceValuePence, variantId: VARIANTS[faceValuePence] };
  }

  function desiredGift(cart, choice) {
    if (cart.currency !== 'GBP' || !choice || !['delivery', 'card'].includes(choice.kind)) return null;
    const parents = cart.lines.filter(line => line.key === choice.parentKey && !promotional(line));
    if (parents.length !== 1) return null;
    const parent = parents[0];
    const eligibility = offer(parent, cart.currency);
    if (!eligibility || (choice.kind === 'delivery' && parent.deliveryExcluded === true)) return null;
    return {
      kind: choice.kind,
      parentKey: parent.key,
      productId: choice.kind === 'delivery' ? DELIVERY_PRODUCT : GIFT_CARD_PRODUCT,
      variantId: choice.kind === 'delivery' ? DELIVERY_VARIANT : eligibility.variantId,
      quantity: 1,
      faceValuePence: choice.kind === 'card' ? eligibility.faceValuePence : 0
    };
  }

  function reconcile(cart, choice) {
    const desired = desiredGift(cart, choice);
    const gifts = cart.lines.filter(promotional);
    const valid = gifts.length === 1 && desired &&
      id(gifts[0].productId) === desired.productId &&
      id(gifts[0].variantId) === desired.variantId && gifts[0].quantity === 1 &&
      gifts[0].originalLinePence === desired.faceValuePence &&
      gifts[0].parentKey === desired.parentKey && netLinePence(gifts[0]) === 0;
    return {
      desired,
      choice: desired ? choice : null,
      removeKeys: valid ? [] : gifts.map(line => line.key),
      add: valid ? null : desired,
      reason: choice && !desired ? 'ineligible' : gifts.some(line => netLinePence(line) !== 0) ? 'not_free' :
        gifts.length > 1 || gifts.some(line => line.quantity !== 1) ? 'one_gift' :
        desired && gifts.length && !valid ? 'updated' : null
    };
  }

  return Object.freeze({ pence, offer, promotional, netLinePence, desiredGift, reconcile,
    variants: VARIANTS, thresholds: THRESHOLDS, deliveryProduct: DELIVERY_PRODUCT, deliveryVariant: DELIVERY_VARIANT,
    giftCardProduct: GIFT_CARD_PRODUCT });
});
